import {
  MessageCircle,
  Target,
  Calendar,
  AlertTriangle,
  Check,
  Settings,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { useTouchpointTracking, GoalStatus } from '@/hooks/use-touchpoint-tracking';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface TouchpointScoreCardProps {
  clientId: string;
  className?: string;
  onEditGoal?: () => void;
}

function getGoalStatusConfig(status: GoalStatus): {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ComponentType<{ className?: string }>;
} {
  switch (status) {
    case 'on_track':
      return {
        label: 'On Track',
        color: 'text-success',
        bgColor: 'bg-success/10 border-success/30',
        icon: Check,
      };
    case 'minimum':
      return {
        label: 'Minimum Met',
        color: 'text-warning',
        bgColor: 'bg-warning/10 border-warning/30',
        icon: Check,
      };
    case 'below_goal':
      return {
        label: 'Below Goal',
        color: 'text-destructive',
        bgColor: 'bg-destructive/10 border-destructive/30',
        icon: AlertTriangle,
      };
    default:
      return {
        label: 'No Goal Set',
        color: 'text-muted-foreground',
        bgColor: 'bg-muted/50 border-border',
        icon: Target,
      };
  }
}

export function TouchpointScoreCard({
  clientId,
  className,
  onEditGoal,
}: TouchpointScoreCardProps) {
  const { stats, loading, error } = useTouchpointTracking(clientId);

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return null;
  }

  const goalConfig = getGoalStatusConfig(stats.goalStatus);
  const StatusIcon = goalConfig.icon;
  const progressPercentage = stats.goal
    ? Math.min(100, (stats.thisWeek / stats.goal.targetWeekly) * 100)
    : 0;

  return (
    <Card className={cn('border', goalConfig.bgColor, className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Touchpoints
          </span>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn('text-xs', goalConfig.color)}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {goalConfig.label}
            </Badge>
            {onEditGoal && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEditGoal}>
                <Settings className="h-4 w-4 text-muted-foreground" />
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Main score */}
        <div className="flex items-center justify-center py-4">
          <div className="relative w-28 h-28">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="56"
                cy="56"
                r="48"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-muted"
              />
              <circle
                cx="56"
                cy="56"
                r="48"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${(progressPercentage / 100) * 301.6} 301.6`}
                className={goalConfig.color}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn('text-2xl font-bold', goalConfig.color)}>
                {stats.thisWeek}
              </span>
              {stats.goal && (
                <span className="text-xs text-muted-foreground">
                  / {stats.goal.targetWeekly} goal
                </span>
              )}
              <span className="text-xs text-muted-foreground mt-1">this week</span>
            </div>
          </div>
        </div>

        {/* Progress bar (if goal set) */}
        {stats.goal && (
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Week Progress</span>
              <span>
                {stats.thisWeek} / {stats.goal.targetWeekly}
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">
                Min: {stats.goal.minWeekly}
              </span>
              <span className="text-muted-foreground">
                Target: {stats.goal.targetWeekly}
              </span>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 text-center text-sm">
          <div className="bg-card/50 rounded p-2">
            <div className="text-muted-foreground text-xs">Last Week</div>
            <div className="font-semibold">{stats.lastWeek}</div>
          </div>
          <div className="bg-card/50 rounded p-2">
            <div className="text-muted-foreground text-xs">30 Day Total</div>
            <div className="font-semibold">{stats.total}</div>
          </div>
        </div>

        {/* Last contact */}
        <div className="mt-4 pt-4 border-t border-white/50 text-center">
          <div className="flex items-center justify-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>
              {stats.lastTouchpointDate ? (
                <>
                  Last contact{' '}
                  <span className="font-medium">
                    {stats.daysSinceContact === 0
                      ? 'today'
                      : formatDistanceToNow(new Date(stats.lastTouchpointDate), {
                          addSuffix: true,
                        })}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">No touchpoints recorded</span>
              )}
            </span>
          </div>
        </div>

        {/* Alert for overdue */}
        {stats.goal &&
          stats.daysSinceContact !== null &&
          stats.daysSinceContact > stats.goal.alertThresholdDays && (
            <div className="mt-3 p-2 bg-destructive/15 text-destructive rounded text-xs flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span>
                No contact in {stats.daysSinceContact} days (threshold: {stats.goal.alertThresholdDays})
              </span>
            </div>
          )}
      </CardContent>
    </Card>
  );
}
